import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import {
    AlertListService,
    I18nHelperService,
    QueryStringService,
    SeoServiceService,
} from "@xref/ngx";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";

import { MarketingService } from "../../services/marketing/marketing.service";
import {
    RegisterService,
    SetUpFormErrors,
} from "../../services/register/register.service";
import { hasOneUppercaseValidator } from "./components/details/has-one-uppercase-validator";
import { MaintenanceService } from "../../services/maintenance/maintenance.service";
import {
    lowercaseCharacterValidator,
    numberCharacterValidator,
    specialCharacterValidator
} from "./components/details/password_validator";
import { ActivatedRoute, Router } from "@angular/router";
import { PermissionsService } from "../../services/permissions/permissions.service";

@Component({
    selector: "xlog-register",
    templateUrl: "./register.component.pug",
    styleUrls: ["./register.component.scss"],
    standalone: false
})
export class RegisterComponent implements OnInit {
    @ViewChild("termsConditionsTmpl") termsConditionsTmpl: any;

    termsConditionsModalRef: BsModalRef;

    formStage: "setUp" | "details" | "thankYou" | "pendingVerification" = "setUp";

    setUpForm = this.fb.group(
        {
            firstName: ["", Validators.required],
            lastName: ["", Validators.required],
            email: ["", [Validators.required, Validators.email]],
            country: ["", [Validators.required]],
            phoneCountry: ["", Validators.required],
            phoneNumber: ["", Validators.required],
            overrideDomainWarning: [false],
            dataRegion: ["", [Validators.required]],
        },
        {
            updateOn: "submit",
        }
    );

    companyDetailsForm = this.fb.group({
        organisation: ["", Validators.required],
        numberOfStaff: ["", Validators.required],
        password: [
            "",
            {
                validators: [
                    Validators.required,
                    Validators.minLength(8),
                    hasOneUppercaseValidator,
                    specialCharacterValidator(),
                    lowercaseCharacterValidator(),
                    numberCharacterValidator()
                ],
                updateOn: "change",
            },
        ],
        termsConditions: [false, Validators.requiredTrue],
    });

    detailsForm = this.fb.group({
        userDetails: this.setUpForm,
        companyDetails: this.companyDetailsForm,
    });

    submitting = false;

    constructor(
        private fb: UntypedFormBuilder,
        private reg: RegisterService,
        private alertList: AlertListService,
        private i18nHelper: I18nHelperService,
        private bsModal: BsModalService,
        private marketing: MarketingService,
        private maintenance: MaintenanceService,
        private qString: QueryStringService,
        private route: ActivatedRoute,
        private router: Router,
        private featureFlagService: PermissionsService,
        private seoService: SeoServiceService
    ) {}

    ngOnInit() {
        this.router.navigate(['/signup']);

        // Update meta tags
        this.route.data.subscribe((data) => {
            this.seoService.updateDescription(data.description);
            this.seoService.updateTitle(data.title);
        });

        if (this.maintenance.inMaintenance && !this.marketing.isClassic) {
            this.maintenance.setMaintenance();
        } else {
            this.marketing.setMarketingResource("register-marketing-section");
            this.preFillEmail();
        }
    }

    resetSetUpFormErrors() {
        this.reg.setUpFormErrors = new SetUpFormErrors();
    }

    submitSetUpSuccess = (data) => {
        this.submitting = false;
        this.formStage = "details";
    };

    submitSetUpError = (err) => {
        this.submitting = false;

        // Use one pointer for all errors,
        // Assumes api always sends at least one of these two error types and never both
        const errors = err.error.email || err.error.non_field_errors;

        // Check for accounts not yet verified
        if (errors.includes("ERROR_PENDING_VERIFICATION")) {
            // Show "pending verification" message
            this.formStage = "pendingVerification";
        } else if (errors.includes("ERROR_EMAIL_EXISTS")) {
            // Show alert message when an account with same email already exists (includes both Lite and Enterprise)
            this.alertList.append([
                {
                    active: true,
                    type: "danger",
                    messageKey: "register.setUp.form.email.errors.alreadyExists",
                    timeout: 0,
                },
            ]);
        } else {
            // Populate all other errors from server, handled in SetUpComponent
            errors.forEach((item: any) => (this.reg.setUpFormErrors[item] = true));
        }
    };

    // Use API to validate account setup details
    submitSetUp() {
        this.resetSetUpFormErrors();
        this.alertList.clear();

        const formData = {
            first_name: this.setUpForm.controls.firstName.value,
            last_name: this.setUpForm.controls.lastName.value,
            email: this.setUpForm.controls.email.value,
            phone: `${this.setUpForm.controls.phoneCountry.value} ${this.setUpForm.controls.phoneNumber.value}`,
            override_domain_is_enterprise_warning:
        this.setUpForm.controls.overrideDomainWarning.value,
        };

        if (this.setUpForm.valid) {
            this.submitting = true;
            this.reg
                .validateSetUp(formData)
                .subscribe(this.submitSetUpSuccess, this.submitSetUpError);
        }
    }

    submitRegisterSuccess = (data) => {
        this.submitting = false;
        this.formStage = "thankYou";
    };

    submitRegisterError = (err) => {
        this.submitting = false;
        console.log(err);
    };

    // Submit all account details to register
    submitRegister() {
        const formData = {
            user_details: {
                first_name: this.setUpForm.controls.firstName.value,
                last_name: this.setUpForm.controls.lastName.value,
                email: this.setUpForm.controls.email.value,
                phone: `${this.setUpForm.controls.phoneCountry.value} ${this.setUpForm.controls.phoneNumber.value}`,
                override_domain_is_enterprise_warning:
          this.setUpForm.controls.overrideDomainWarning.value,
            },
            company_details: {
                organisation: this.companyDetailsForm.controls.organisation.value,
                num_staff: this.companyDetailsForm.controls.numberOfStaff.value,
                country_id: this.setUpForm.controls.country.value,
                password: this.companyDetailsForm.controls.password.value,
            },
            accepted_terms_and_conditions:
        this.companyDetailsForm.controls.termsConditions.value,
            default_locale: this.i18nHelper.locale,
        };

        if (this.companyDetailsForm.valid) {
            this.submitting = true;
            const region = this.setUpForm.controls.dataRegion.value;

            // Set regional url based on chosen data region before submitting to API
            this.reg.setRegionalUrl(region);

            this.reg
                .register(formData)
                .subscribe(this.submitRegisterSuccess, this.submitRegisterError);
        }
    }

    resetToSetup() {
        this.formStage = "setUp";
    }

    showTermsModal() {
        this.termsConditionsModalRef = this.bsModal.show(this.termsConditionsTmpl);
    }

    termsConditionsAccepted(accepted: boolean) {
        if (accepted) {
            this.companyDetailsForm.controls.termsConditions.setValue(true);
            this.termsConditionsModalRef.hide();
        }
    }

    termsConditionsCancel() {
        this.termsConditionsModalRef.hide();
    }

    private preFillEmail() {
        const email = decodeURIComponent(this.qString.getParam("email"));

        // HTML5 email validation regex https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#Basic_validation
        const emailRegEx =
      /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

        // Only pre fill if email has value, input validation will block invalid emails
        if (email && email != null && email != "null") {
            this.setUpForm.controls.email.setValue(email);

            // Only register if email passes HTML5 email validation spec
            if (emailRegEx.test(email)) this.reg.registerLead(email).subscribe();
        }
    }
}
